'use client';

import cx from 'classnames';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { Link } from '~/ui/components/link';
import typographyStyles from '~/ui/styles/typography.module.scss';

import styles from './markdown.module.scss';

type MarkdownChildren = React.ComponentProps<typeof ReactMarkdown>['children'];

export interface MarkdownProps extends React.ComponentProps<'div'> {
  children?: MarkdownChildren | Array<MarkdownChildren>;
  customComponents?: Components;
}

const defaultComponents: Components = {
  table: () => null,
  hr: () => null,
  pre: () => null,
  blockquote: () => null,
  a: props => (
    <Link href={props.href as string} className={styles.link}>
      {props.children}
    </Link>
  ),
  p: props => <p className={cx(styles.paragraph, typographyStyles.paragraph)}>{props.children}</p>,
  h1: props => (
    <h2 className={cx(styles.heading1, typographyStyles.modulesTitle)}>{props.children}</h2>
  ),
  h2: props => (
    <h3 className={cx(styles.heading2, typographyStyles.bodyLarge)}>{props.children}</h3>
  ),
  h3: props => (
    <h4 className={cx(styles.heading3, typographyStyles.paragraph, styles.bold)}>
      {props.children}
    </h4>
  ),
  li: props => <li className={typographyStyles.paragraph}>{props.children}</li>,
};

export function Markdown({ className, children, customComponents, ...props }: MarkdownProps) {
  let content: MarkdownChildren;
  if (Array.isArray(children)) {
    content = children.filter(Boolean).join('\n');
  } else {
    content = children;
  }

  return (
    <div {...props} className={cx(styles.markdown, className)}>
      <ReactMarkdown
        components={{
          ...defaultComponents,
          ...customComponents,
        }}
        remarkPlugins={[remarkBreaks, remarkGfm]}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
